import { useEffect } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useMessageStore } from '../zustandStore/messageStore';
import ApiError from '../lib/ApiError';
import { ApiResultMessages } from './enums';

interface CustomQueryOptions<TData, TError> extends UseQueryOptions<TData, TError> {
	showRetryMessageIfError?: boolean;
}

const useErrorHandling = <TData, TError>(
	queryResult: UseQueryResult<TData, TError>,
	options: CustomQueryOptions<TData, TError>
) => {
	const updateMessage = useMessageStore((state) => state.updateMessage);
	const { error } = queryResult;

	useEffect(() => {
		if (error) {
			// Ignore AbortError - this occurs when the user navigates away before an async operation completes
			const errorName = (error as Error).name;
			const errorMessage = (error as Error).message ?? '';
			if (errorName === 'AbortError' || errorMessage.includes('AbortError')) return;

			if (options.showRetryMessageIfError) updateMessage(ApiResultMessages.GeneralRetryMessage);
			else updateMessage((error as ApiError).body ?? errorMessage ?? 'An error occurred');
		}
	}, [error, updateMessage]);
};

const useCustomQuery = <TData = unknown, TError = Error>(options: CustomQueryOptions<TData, TError>): TData => {
	const queryResult = useQuery({ ...options });
	useErrorHandling(queryResult, options);
	return queryResult.data;
};

// Returns full query result for component-level loading patterns (skeletons, shimmers)
export const useCustomQueryResult = <TData = unknown, TError = Error>(
	options: CustomQueryOptions<TData, TError>
): UseQueryResult<TData, TError> => {
	const queryResult = useQuery({ ...options });
	useErrorHandling(queryResult, options);
	return queryResult;
};

export default useCustomQuery;
